import { useRefs, useEvent } from '../framework'
import { events } from '../events'
import {
  getConfiguratorOptions,
  getCurrentOptions,
  getProductConfiguratorData,
  setOption,
  translateUrlVariantToOptions,
} from '../configurator'

export default async ref => {
  if (!ref.variantSelector) return

  await fetchAndAppendColorLists(ref)

  await translateUrlVariantToOptions()

  const allData = await getConfiguratorOptions()
  const productData = await getProductConfiguratorData()

  ref.variantSelector.forEach(variantSelector => {
    const refs = useRefs({ target: variantSelector, asArray: true })

    updateUI(refs, allData)

    refs.selector.forEach(button => {
      button.addEventListener('click', () => {
        const { type, handle, family } = button.dataset

        const h = family ? getFirstColor(family, allData) : handle

        setOption(type, h)
        updateUI(refs, allData)
        useEvent.dispatch(events.product.variantSelectorUpdate)
      })
    })

    function handleAvailableOnline() {
      const url = new URL(window.location)

      if (!url.searchParams.has('available')) {
        // activate all
        refs.selector.forEach(button => button.classList.remove('disabled'))
        return
      }

      if (!productData) return

      const availableOptions = productData.variants
        .map(variant => variant.options)
        .flat()
        .reduce((acc, cur) => {
          if (!acc[cur.type]) acc[cur.type] = []

          if (!acc[cur.type].includes(cur.handle)) {
            acc[cur.type].push(cur.handle)
          }

          return acc
        }, {})

      const availableFamilies = [
        ...new Set(
          productData.product_options
            .find(option => option.type === 'upholstery')
            ?.options?.filter(option => {
              return availableOptions['upholstery']?.includes(option.handle)
            })
            .map(option => option.family)
        ),
      ]

      refs.selector.forEach(button => {
        const { type, handle, family } = button.dataset

        if (family) {
          button.classList.toggle(
            'disabled',
            !availableFamilies.includes(family)
          )
        } else {
          button.classList.toggle(
            'disabled',
            !availableOptions[type]?.includes(handle)
          )
        }
      })
    }

    handleAvailableOnline()

    useEvent.listen(events.product.availableOnline, handleAvailableOnline)
  })

  return () => {}
}

async function updateUI(refs, allData) {
  const currentOptions = await getCurrentOptions({ asObject: true })

  // console.log('currentOptions', allData, currentOptions)

  // activate initially chosen options
  activateButtons(refs, currentOptions)

  // show the initial chosen color column
  activateColorList(refs, currentOptions)

  // prints the name of the current choice in the mobile dropup
  changeCurrentOptionMobileLabel(refs, currentOptions, allData)

  // disable options if another option overwrites
  disableOverwrittenOptions(refs, currentOptions)

  // show/hide information about leather prices
  toggleLeatherDisclaimer(refs, currentOptions)
}

function activateButtons(refs, currentOptions) {
  // console.log('currentOptions', currentOptions)

  refs.selector.forEach(button => {
    const { type, handle, family } = button.dataset

    if (family) {
      // activate if the button is part of the chosen family (shell)
      button.classList.toggle('active', currentOptions[type]?.family === family)
    } else {
      // if (family === 'polypropylene') {
      //   console.log(type, handle, family)
      // }
      // activate if the button is the chosen option
      button.classList.toggle('active', currentOptions[type]?.handle === handle)
    }
  })
}

function activateColorList(refs, currentOptions) {
  if (!refs.child) return

  refs.child.forEach(list => {
    if (currentOptions.shell?.handle === 'polypropylene') {
      const { type, parent } = list.dataset

      list.classList.toggle(
        'hidden',
        type !== 'shell' || currentOptions.shell?.handle !== parent
      )

      return
    }

    const { type, parent } = list.dataset

    list.classList.toggle('hidden', currentOptions[type]?.family !== parent)
  })
}

function changeCurrentOptionMobileLabel(refs, currentOptions, allData) {
  // doesn't exist in the desktop variant selector
  if (!refs.currentChoice) return

  refs.currentChoice.forEach(label => {
    const { choice } = label.dataset

    const noUpholstery = currentOptions.shell?.handle === 'no-upholstery'

    if (choice === 'upholstery-family') {
      const family = allData.upholstery?.find(
        shell => shell.handle === currentOptions.upholstery?.family
      )?.name

      label.textContent = !noUpholstery && family ? family : ''

      return
    }

    if (choice === 'upholstery' && noUpholstery) {
      label.textContent = ''
      return
    }

    if (!currentOptions[choice]) {
      label.textContent = ''
      return
    }

    label.textContent = currentOptions[choice].name
  })
}

function disableOverwrittenOptions(refs, currentOptions) {
  if (!refs.group) return

  refs.group.forEach(group => {
    const { disabledBy } = group.dataset

    if (!disabledBy) {
      group.classList.remove('disabled')
      return
    }

    const options = disabledBy.split(';')

    const disable = options.find(option => {
      const [type, handle] = option.split(':')

      return currentOptions[type]?.handle === handle
    })

    group.classList.toggle('disabled', !!disable)
  })
}

function toggleLeatherDisclaimer(refs, currentOptions) {
  refs.leatherDisclaimer.forEach(element => {
    element.classList.toggle(
      'hidden',
      !currentOptions.upholstery ||
        currentOptions.upholstery?.material !== 'Leather'
    )
  })
}

async function fetchAndAppendColorLists(ref) {
  const colorListContainer = ref.variantSelector[0].querySelector(
    '[data-pages-amount]'
  )

  if (!colorListContainer?.dataset) return

  const pages = [...Array(parseInt(colorListContainer?.dataset.pagesAmount))]

  const result = []

  const getPage = async pageParam => {
    const url = new URL(window.location.pathname, window.location.origin)
    url.searchParams.set('section_id', 'configurator-product-upholstery-html')

    if (pageParam) {
      url.searchParams.set(pageParam[0], pageParam[1])
    }

    const text = await fetch(url.toString()).then(res => res.text())
    const markup = new DOMParser().parseFromString(text, 'text/html')

    const nextPage = markup.querySelector('#next_page')?.href

    let nextPageParam

    if (nextPage) {
      const url = new URL(nextPage)
      nextPageParam = Object.fromEntries(url.searchParams.entries())
    }

    return {
      result: markup.querySelectorAll('ul'),
      nextUrl: nextPageParam && Object.entries(nextPageParam)[0],
    }
  }

  let nextPage

  for (const page in pages) {
    const res = await getPage(nextPage)

    result.push(res.result)

    nextPage = res.nextUrl
  }

  // append copies of all the lists

  result.forEach(page => {
    page.forEach(list => {
      const parents = document.body.querySelectorAll(
        `[data-parent="${list.dataset.parent}"]`
      )
      parents.forEach(parent => {
        parent.append(...list.cloneNode(true).children)
      })
    })
  })

  return result
}

function getFirstColor(family, allData) {
  return allData.upholstery.find(item => item.handle === family)?.colors[0]
    ?.handle
}
